// app/frontend/components/ui/Forms/L2KButton.tsx
import { Button, ButtonProps } from "@mantine/core";
import { ReactNode, forwardRef } from "react";

export interface L2KButtonProps
  extends Omit<
    React.ComponentPropsWithoutRef<"button"> & ButtonProps,
    "variant"
  > {
  children: ReactNode;
  variant?: "primary" | "secondary" | "outline" | "ghost";
}

export const L2KButton = forwardRef<HTMLButtonElement, L2KButtonProps>(
  ({ children, variant = "primary", styles, ...props }, ref) => {
    const variantStyles = {
      primary: {
        root: {
          background: "linear-gradient(135deg, #FFC107 0%, #FF9800 100%)",
          color: "#171717",
          fontWeight: 600,
          fontFamily: "'Public Sans', sans-serif",
          border: "none",
          "&:hover": {
            background: "linear-gradient(135deg, #FFD54F 0%, #FFA726 100%)",
          },
          "&:disabled": {
            background: "rgba(255, 255, 255, 0.1)",
            color: "#666",
          },
        },
      },
      secondary: {
        root: {
          backgroundColor: "rgba(255, 152, 0, 0.15)",
          color: "#FF9800",
          fontWeight: 600,
          fontFamily: "'Public Sans', sans-serif",
          border: "1px solid rgba(255, 152, 0, 0.3)",
          "&:hover": {
            backgroundColor: "rgba(255, 152, 0, 0.25)",
          },
          "&:disabled": {
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            color: "#666",
            borderColor: "rgba(255, 255, 255, 0.1)",
          },
        },
      },
      outline: {
        root: {
          backgroundColor: "transparent",
          color: "#FF9800",
          fontWeight: 600,
          fontFamily: "'Public Sans', sans-serif",
          border: "1px solid #FF9800",
          "&:hover": {
            backgroundColor: "rgba(255, 152, 0, 0.1)",
          },
          "&:disabled": {
            color: "#666",
            borderColor: "#666",
          },
        },
      },
      ghost: {
        root: {
          backgroundColor: "transparent",
          color: "#D0D0D0",
          fontWeight: 500,
          fontFamily: "'Public Sans', sans-serif",
          border: "none",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            color: "#FF9800",
          },
          "&:disabled": {
            color: "#666",
          },
        },
      },
    };

    return (
      <Button
        ref={ref}
        radius="md"
        styles={{
          ...variantStyles[variant],
          ...styles,
        }}
        {...props}
      >
        {children}
      </Button>
    );
  }
);

L2KButton.displayName = "L2KButton";

export default L2KButton;
